/**
 * Template Factory — the orchestrator.
 *
 * manufacture() runs the full production line:
 *
 *   variation → DNA → duplicate check → DesignSpec synthesis
 *     → AI Design Engine (generateTemplate, read-only use)
 *     → quality filter (reject < 90) → image queries → history
 *
 * and retries rejected candidates until the requested batch is filled or
 * the attempt budget runs out. Output is DesignSpecs + metadata only —
 * nothing here renders, and the Design Engine is never modified.
 */

import { generateTemplate } from '../aiDesignEngine';
import { fingerprint } from './designDNAEngine';
import { generateDNA, createRng } from './variationEngine';
import { checkDuplicate, DEFAULT_SIMILARITY_THRESHOLD } from './duplicateDetectionEngine';
import { getCategory } from './categoryEngine';
import { getTypographyTheme } from './typographyThemeEngine';
import { getDecorationPack } from './decorationPackEngine';
import { buildImageQueries } from './imageQueryEngine';
import { evaluateQuality, DEFAULT_MIN_SCORE } from './qualityFilter';
import { GenerationHistory } from './generationHistory';

const DEFAULT_CANVAS = { width: 1080, height: 1350 };

/** Shared history — the admin console and repeated batches accumulate here. */
export const factoryHistory = new GenerationHistory({
  storageKey: 'aiTemplateFactory.history.v1',
});

const applyOfferStyle = (offerText, style) =>
  style === 'ribbon-text' ? `★ ${offerText} ★` : offerText;

const applyFooterStyle = (footerText, style) =>
  style === 'minimal' ? footerText.split('·')[0].trim() : footerText;

/**
 * Turn DNA + category into a complete Design Engine DesignSpec.
 * Exported on its own so later code (or OpenAI-supplied copy) can re-run
 * synthesis with custom content while keeping the same DNA.
 */
export const synthesizeSpec = (dna, categoryId, { canvas = DEFAULT_CANVAS, content = null, rng = createRng() } = {}) => {
  const cat = getCategory(categoryId);
  const typographyTheme = getTypographyTheme(dna.typographyTheme);
  const decorationPack = getDecorationPack(dna.decorationPack);
  const copy = content || cat.content[Math.floor(rng() * cat.content.length)];

  const components = [];
  if (dna.offerStyle === 'price-circle') {
    components.push({ id: 'priceTag', zone: 'content', props: { price: copy.offer } });
  }
  if (dna.footerStyle === 'social') {
    components.push({ id: 'socialIcons', zone: 'footer', props: {} });
  }
  if (dna.imageCount >= 1) {
    components.push({ id: 'frame', zone: 'hero', props: { label: `${cat.name} hero image` } });
  }
  if (dna.imageCount >= 2) {
    components.push({ id: 'frame', zone: 'content', props: { label: `${cat.name} detail image` } });
  }

  return {
    canvas: { ...canvas },
    layout: dna.layout,
    palette: { seed: dna.seed, harmony: dna.harmony, dark: dna.dark },
    typography: {
      pairingId: typographyTheme.pairingId,
      baseSize: Math.round(canvas.width / 67),
      scale: dna.typeScale,
    },
    spacing: dna.spacing,
    decoration: decorationPack.engineStyle,
    content: {
      primaryHeading: { text: copy.primaryHeading },
      secondaryHeading: { text: copy.secondaryHeading },
      body: { text: copy.body },
      offer: { text: applyOfferStyle(copy.offer, dna.offerStyle) },
      cta: { text: copy.cta },
      footer: { text: applyFooterStyle(copy.footer, dna.footerStyle) },
    },
    components,
  };
};

/**
 * Manufacture a batch of unique, quality-gated template specs.
 *
 * options = {
 *   category            (required) — categoryEngine id
 *   count = 1           — how many accepted templates to return
 *   canvas              — { width, height }
 *   minScore = 90       — quality gate (product rule)
 *   similarityThreshold = 0.7 — DNA similarity above this ⇒ duplicate
 *   seed                — RNG seed; same seed + same history ⇒ same batch
 *   overrides = {}      — pin any DNA gene (the future OpenAI hook)
 *   history             — GenerationHistory (defaults to the shared one)
 *   maxAttempts         — retry budget (default count × 40)
 * }
 *
 * Returns { templates, stats, complete } where each template is
 * { designSpec, dna, fingerprint, qualityScore, quality, validation, imageQueries }.
 */
export const manufacture = ({
  category,
  count = 1,
  canvas = DEFAULT_CANVAS,
  minScore = DEFAULT_MIN_SCORE,
  similarityThreshold = DEFAULT_SIMILARITY_THRESHOLD,
  seed = Date.now(),
  overrides = {},
  history = factoryHistory,
  maxAttempts = count * 40,
} = {}) => {
  getCategory(category); // fail fast on unknown category

  const rng = createRng(seed);
  const templates = [];
  const batchDnas = [];
  const stats = { attempts: 0, accepted: 0, rejectedDuplicate: 0, rejectedQuality: 0 };

  while (templates.length < count && stats.attempts < maxAttempts) {
    stats.attempts++;

    const dna = generateDNA(category, rng, overrides);
    const fp = fingerprint(dna);

    if (history.hasFingerprint(fp)) {
      stats.rejectedDuplicate++;
      continue;
    }
    const { duplicate } = checkDuplicate(
      dna,
      [...history.candidatesFor(category), ...batchDnas],
      similarityThreshold
    );
    if (duplicate) {
      stats.rejectedDuplicate++;
      continue;
    }

    const designSpec = synthesizeSpec(dna, category, { canvas, rng });
    const result = generateTemplate(designSpec);
    const verdict = evaluateQuality(result, { minScore });
    if (!verdict.passed) {
      stats.rejectedQuality++;
      continue;
    }

    history.add({ category, dna, fingerprint: fp, score: result.quality.score });
    batchDnas.push(dna);
    templates.push({
      designSpec,
      dna,
      fingerprint: fp,
      qualityScore: result.quality.score,
      quality: result.quality,
      validation: result.validation,
      imageQueries: buildImageQueries(category, dna),
    });
  }

  stats.accepted = templates.length;
  return { templates, stats, complete: templates.length === count };
};
